import { createApi } from "@reduxjs/toolkit/query/react";

import { IDeployTopologyTypeArgs } from "../redux/actions/NewTopologyActions";
import { baseQuery } from "./baseApi";
import {
  IGetAvailableQuotaInitialResponse,
  IGetAvailableQuotaParams,
  IGetAvailableQuotaResponse,
  IGetBlessedTemplateParams,
  IGetBlessedTemplateResponse,
  IGetBuildNumbersParams,
  IGetBuildNumbersResponse,
  IGetServiceStatusResponse,
  IGetTopologyParams,
  IGetTopologyResponse,
  IPauseTaskParams,
  IResetTaskParams,
  ISkipTaskParams,
} from "./tdsApi.types";

export const tdsApi = createApi({
  reducerPath: "tdsApi",
  tagTypes: ["Topology"],
  baseQuery,
  endpoints: (builder) => ({
    getServiceStatus: builder.query<IGetServiceStatusResponse, void>({
      query: () => ({
        url: "api/servicestatus",
      }),
    }),

    getAvailableQuota: builder.query<IGetAvailableQuotaResponse, IGetAvailableQuotaParams>({
      query: (p) => ({
        url: "api/quota/available",
        params: {
          user: p.User.upn,
        },
        environment: p.Environment,
      }),
      transformResponse: (initialResponse: IGetAvailableQuotaInitialResponse) => {
        const { AvailableQuota, DefaultQuota, MaximumQuota } = initialResponse[0];

        return {
          AvailableQuota,
          DefaultQuota,
          MaximumQuota,
        };
      },
    }),

    getTopology: builder.query<IGetTopologyResponse, IGetTopologyParams>({
      query: (p) => ({
        url: "api/topology",
        params: {
          id: p.TopologyId,
        },
        environment: p.Environment,
      }),
      providesTags: ["Topology"],
    }),

    resetTask: builder.mutation<void, IResetTaskParams>({
      query: (p) => ({
        url: "api/task/reset",
        params: {
          taskId: p.TaskId,
        },
        method: "GET",
        environment: p.Environment,
      }),
      invalidatesTags: ["Topology"],
    }),

    skipTask: builder.mutation<void, ISkipTaskParams>({
      query: (p) => ({
        url: "api/task/skip",
        params: {
          taskId: p.TaskId,
        },
        environment: p.Environment,
      }),
      invalidatesTags: ["Topology"],
    }),

    pauseTask: builder.mutation<void, IPauseTaskParams>({
      query: (p) => ({
        url: "api/task/pause",
        params: {
          taskId: p.TaskId,
          paused: p.PauseTask,
        },
        environment: p.Environment,
      }),
      invalidatesTags: ["Topology"],
    }),

    getBuildNumbers: builder.mutation<IGetBuildNumbersResponse, IGetBuildNumbersParams>({
      query: (p) => ({
        url: "api/buildnumber/bytopologytype",
        method: "POST",
        body: p.TopologyType,
        environment: p.TopologyType.environment,
      }),
    }),

    getBlessedTemplate: builder.mutation<IGetBlessedTemplateResponse, IGetBlessedTemplateParams>({
      query: (p) => ({
        url: "api/template/blessed",
        method: "POST",
        params: {
          user: p.User,
        },
        body: p.TopologyType,
        environment: p.TopologyType.environment,
      }),
    }),

    deployTopologyType: builder.mutation<void, IDeployTopologyTypeArgs>({
      query: (p) => ({
        url: "api/topologytype/deploy",
        method: "POST",
        body: { ...p, Source: "v2website" },
        environment: p.TopologyType.environment,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),

    deployTopologyTypeWithQueue: builder.mutation<void, IDeployTopologyTypeArgs>({
      query: (p) => ({
        url: "api/topologytype/deploywithqueue",
        method: "POST",
        body: { ...p, Source: "v2website" },
        environment: p.TopologyType.environment,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
  }),
});

export const {
  useLazyGetTopologyQuery,
  useGetServiceStatusQuery,
  useGetBuildNumbersMutation,
  useGetBlessedTemplateMutation,
  usePauseTaskMutation,
  useResetTaskMutation,
  useSkipTaskMutation,
  useDeployTopologyTypeMutation,
  useLazyGetAvailableQuotaQuery,
  useDeployTopologyTypeWithQueueMutation
} = tdsApi;
